import { Play, Share2, Download } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL } from "../../lib/api";
import { Brand, brand, brandShort } from "../../context/CompanyContext";

const TUTORIAL_ID = "guia-cliente-luxstay";
const VIDEO_URL = `${BACKEND_URL}/api/tutorials/${TUTORIAL_ID}/video.mp4`;

/**
 * ClientTutorial — public-access video tutorial for clients.
 * The video endpoint is unauthenticated so mobile browsers and share
 * recipients can play it without cookies. We use native <video controls>
 * with autoPlay + playsInline so tapping the play button works on iOS.
 */
export default function ClientTutorial() {
  async function share() {
    const text = `Guia do cliente ${brand()} — tudo o que precisa de saber em 2 minutos.`;
    if (navigator.share) {
      try {
        await navigator.share({ title: `Tutorial ${brandShort()}`, text, url: VIDEO_URL });
        return;
      } catch (e) {
        if (e.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(VIDEO_URL);
      toast.success("Link copiado");
    } catch {
      toast.error("Não foi possível copiar");
    }
  }

  return (
    <div className="space-y-8" data-testid="client-tutorial-page">
      <div>
        <div className="eyebrow mb-3 text-[#d4af37]">Tutorial</div>
        <h1 className="font-display text-4xl sm:text-5xl">Como usar o <Brand short /></h1>
        <p className="text-sm text-[#a0b2af] mt-3 max-w-2xl leading-relaxed">
          Um guia curto com narração em português de Portugal que mostra, passo a
          passo, tudo o que pode fazer na área de cliente — criar conta, agendar
          limpezas, pagamentos, relatórios, avaliações e o programa de indicações.
        </p>
      </div>

      <div className="lux-card p-0 overflow-hidden" data-testid="client-tutorial-card">
        <video
          controls
          autoPlay
          playsInline
          preload="auto"
          src={VIDEO_URL}
          poster={`${BACKEND_URL}/assets/email-luxstay-logo.png`}
          className="w-full aspect-video bg-black"
          data-testid="client-tutorial-video"
        />
        <div className="p-5 flex flex-wrap items-center justify-between gap-4 border-t border-[#1a4640]">
          <div className="text-[11px] uppercase tracking-widest text-[#a0b2af] inline-flex items-center gap-2">
            <Play size={12} className="text-[#d4af37]" />
            Guia completo · 2 minutos · PT-PT
          </div>
          <div className="flex items-center gap-2">
            <button onClick={share}
                    className="btn-ghost-gold px-4 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2"
                    data-testid="client-tutorial-share">
              <Share2 size={13} /> Partilhar
            </button>
            <a href={VIDEO_URL} download="luxstay-tutorial.mp4"
               className="btn-ghost-gold px-4 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2"
               data-testid="client-tutorial-download">
              <Download size={13} /> Transferir
            </a>
          </div>
        </div>
      </div>

      <section className="grid md:grid-cols-3 gap-4" data-testid="client-tutorial-highlights">
        {[
          { title: "Criar conta · Agendar", body: "Registar-se em menos de um minuto e marcar a primeira limpeza com data, hora e extras." },
          { title: "Pagamento · Reservas", body: "Pagar online ou por transferência e consultar todas as reservas com estado em tempo real." },
          { title: "Relatório · Avaliação", body: "Ver as fotografias da limpeza, aprovar extras e avaliar o serviço com um clique." },
        ].map((c, i) => (
          <div key={i} className="lux-card p-5">
            <div className="text-[10px] uppercase tracking-widest text-[#d4af37]">Bloco {i + 1}</div>
            <div className="font-display text-lg mt-2">{c.title}</div>
            <div className="text-xs text-[#a0b2af] mt-2 leading-relaxed">{c.body}</div>
          </div>
        ))}
      </section>
    </div>
  );
}
